"use client"

import { translateToEnglish, translateToSpanish } from "../../utils/translations"
import { useState, useEffect, useRef, useCallback } from "react"
import { getCategories } from "../../services/categoriesApi"
import { useNavigate, useParams } from "react-router-dom"
import "./Categories.css"
import Breadcrumb from "../Breadcrumbs/Breadcrumbs"
import { suggest } from "../../services/controlPanelApi"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Spinner from "../Spinner/Spinner"
import Location from "../Location/Location"
import AutocompleteSearch from "../Home/AutocompleteSearch"
import "../Home/AutocompleteSearch.css"
import { List, BarChart2, Home } from 'lucide-react' // icons para botones de orden
// íconos removidos (no usados en este componente)
import { useLocations } from "../../utils/LocationContext" // Importar el hook de ubicaciones
import FeaturedProvidersBanner from "./FeaturedProvidersBanner"
import { Helmet } from "react-helmet-async";

const Categories = () => {
    const { type } = useParams()
    const typeEnglish = translateToEnglish(type)

    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [isUpdating, setIsUpdating] = useState(false) // Estado para controlar operaciones críticas
    const [showSuggestionModal, setShowSuggestionModal] = useState(false)

    // Usar el estado global de ubicación del contexto
    const { selectedGlobalLocation, getLocationByName } = useLocations()

    // Estado para la ubicación seleccionada inicializado con el valor global
    const [selectedLocationObj, setSelectedLocationObj] = useState(selectedGlobalLocation)

    // Ref para verificar si ya se cargaron las categorías para una ubicación
    const categoriesLoadedForLocationRef = useRef(null)

    // Estado para modo de ordenamiento: 'alpha' (alfabético) por defecto, o 'count' (mayor a menor por cantidad)
    const [sortMode, setSortMode] = useState('alpha')

    // Estado para nueva categoría sugerida (formulario de sugerencia)
    const [newCategory, setNewCategory] = useState({ name: "", subcategories: "" })

    // Estados para el autocompletado de búsqueda
    const [searchQuery, setSearchQuery] = useState("")
    const [searchResults, setSearchResults] = useState({})

    const navigate = useNavigate()

    // Actualizar el estado local cuando cambie el global
    useEffect(() => {
        if (selectedGlobalLocation) {
            setSelectedLocationObj(selectedGlobalLocation)
        }
    }, [selectedGlobalLocation])

    // Al cargar Categories, usamos el estado global si existe, o intentamos recuperar del localStorage como fallback
    useEffect(() => {
        // Si no hay ubicación global, intentar recuperar de localStorage como fallback
        if (!selectedLocationObj) {
            const storedLocation = localStorage.getItem("selectedLocation")
            if (storedLocation) {
                try {
                    const parsedLocation = JSON.parse(storedLocation)
                    setSelectedLocationObj(parsedLocation)
                } catch (error) {
                    console.error("Error parsing stored location:", error)
                }
            }
        }
    }, [selectedLocationObj])

    // Sincronizar el objeto de ubicación con el catálogo prefetcheado del LocationContext.
    // Antes se hacía un getLocationByName() al backend; ahora se resuelve localmente.
    useEffect(() => {
        if (!selectedLocationObj?.name) return
        const updatedLocation = getLocationByName(selectedLocationObj.name)
        if (updatedLocation && updatedLocation !== selectedLocationObj) {
            setSelectedLocationObj(updatedLocation)
            localStorage.setItem("selectedLocation", JSON.stringify(updatedLocation))
        }
    }, [selectedLocationObj?.name, getLocationByName])

    // useEffect para obtener las categorías
    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            try {
                // Siempre cargamos las categorías, con o sin ubicación
                // Si hay ubicación, la enviamos; si no, enviamos null o undefined
                const locationName = selectedLocationObj?.name || null;
                const data = await getCategories(typeEnglish, locationName);

                const translatedCategories = data.map((category) => ({
                    ...category,
                    type: translateToSpanish(category.type),
                }));

                setCategories(translatedCategories);

                // Actualizamos la referencia solo si hay ubicación
                if (selectedLocationObj) {
                    categoriesLoadedForLocationRef.current = selectedLocationObj.name;
                } else {
                    categoriesLoadedForLocationRef.current = null;
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setLoading(false);
            }
        };

        // Cargamos categorías al inicio (sin ubicación) o cuando cambia la ubicación
        if (!categoriesLoadedForLocationRef.current ||
            (selectedLocationObj && categoriesLoadedForLocationRef.current !== selectedLocationObj.name) ||
            (!selectedLocationObj && categoriesLoadedForLocationRef.current !== null)) {
            fetchCategories();
        }
    }, [typeEnglish, selectedLocationObj]);

    // Filtrar categorías basadas en la búsqueda
    useEffect(() => {
        if (searchQuery.trim() === "") {
            setSearchResults({});
            return;
        }

        const results = {};
        categories.forEach((category) => {
            if (category.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                // Añadir la categoría con su url para mostrar en los resultados
                results[category.name] = `/${category.type}/${category.name}/proveedores`;
            }
        });

        setSearchResults(results);
    }, [searchQuery, categories]);

    // Callback para el componente Location
    const handleLocationChange = useCallback((locationObj) => {
        // No realizar cambios si estamos actualizando
        if (isUpdating || loading) return;

        // Activar el estado de actualización
        setIsUpdating(true);

        // Si cambió la ubicación, actualizamos y forzamos una recarga de categorías
        if (!locationObj || !selectedLocationObj || locationObj.name !== selectedLocationObj.name) {
            setSelectedLocationObj(locationObj);
            // Resetear la referencia para forzar recarga de categorías
            categoriesLoadedForLocationRef.current = null;

            // Programar la desactivación del estado después de un tiempo
            setTimeout(() => {
                setIsUpdating(false);
            }, 500);
        } else {
            // Si no hay cambios, desactivamos el estado inmediatamente
            setIsUpdating(false);
        }
    }, [selectedLocationObj, isUpdating, loading]);

    const handleCategoryClick = useCallback((categoryName, categoryType) => {
        // No realizar cambios si estamos actualizando
        if (isUpdating || loading) return;

        setIsUpdating(true);
        // Navegar directamente a la lista de proveedores
        navigate(`/${categoryType}/${categoryName}/proveedores`, {
            state: { subcategory: "", location: selectedLocationObj?.name },
        });
        setIsUpdating(false);
    }, [navigate, selectedLocationObj, isUpdating, loading]);

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        const suggestion = {
            suggestion: newCategory.name + " - " + typeEnglish + " - " + newCategory.subcategories,
            type: "Category",
        }
        try {
            await suggest(suggestion)
            toast.success("Sugerencia enviada con éxito")
            setShowSuggestionModal(false)
            setNewCategory({ name: "", subcategories: "" })
        } catch (error) {
            console.error("Error submitting suggestion:", error)
            toast.error("Error al enviar la sugerencia")
        }
        setLoading(false)
    }

    const renderProviderCount = (category) => {
        // Solo mostramos el conteo si hay ubicación seleccionada Y las categorías ya se cargaron para esa ubicación
        if (!selectedLocationObj ||
            categoriesLoadedForLocationRef.current !== selectedLocationObj.name) {
            return null
        }

        // Usar el ID de la categoría para buscar en los conteos
        const categoryId = category.id || category._id || category.name; // Fallback al nombre si no hay ID

        // Verificar que el objeto de ubicación tenga los datos de conteo
        if (typeEnglish === "service") {
            const count = selectedLocationObj.serviceCategoriesCount?.[categoryId]
            return typeof count === 'number' ? count : 0
        } else {
            const count = selectedLocationObj.productsCategoriesCount?.[categoryId]
            return typeof count === 'number' ? count : 0
        }
    }

    // Funciones para el autocompletado
    const handleSearchQueryChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleResultSelect = (result, url) => {
        navigate(url, { state: { subcategory: "", location: selectedLocationObj?.name } });
        setSearchQuery("");
        setSearchResults({});
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && searchQuery) {
            const firstResult = Object.entries(searchResults)[0];
            if (firstResult) {
                // firstResult es [nombreCategoria, url]
                handleResultSelect(firstResult[0], firstResult[1]);
            }
        }
    };

    // Función segura para mostrar el modal
    const handleShowSuggestionModal = useCallback(() => {
        if (isUpdating || loading) return;
        setShowSuggestionModal(true);
    }, [isUpdating, loading]);

    // Función segura para cerrar el modal
    const handleCloseSuggestionModal = useCallback(() => {
        setShowSuggestionModal(false);
    }, []);

    // Meta tags dinámicos
    const locationText = selectedLocationObj?.name || 'Argentina';
    const categoryTypeText = type === "servicios" ? "Servicios" : "Productos";
    const pageTitle = `${categoryTypeText} en ${locationText} | ContactaMe`;
    const pageDescription = `Explorá todas las categorías de ${type} disponibles en ${locationText}. Encontrá profesionales y proveedores locales. ${categories.length} categorías disponibles.`;
    const pageUrl = `https://contactame.com.ar/categorias/${type}`;

    return (
        <div className="categories-wrapper">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta
                    name="keywords"
                    content={`${type}, categorías, ${locationText}, profesionales, proveedores, contactame, argentina`}
                />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:url" content={pageUrl} />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content="https://contactame.com.ar/logo512.png" />

                {/* Canonical URL */}
                <link rel="canonical" href={pageUrl} />
            </Helmet>

            {/* Overlay para bloquear interacciones durante actualización */}
            {isUpdating && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'transparent',
                        zIndex: 9999,
                        cursor: 'wait'
                    }}
                    aria-label="Actualizando contenido, por favor espera"
                />
            )}

            {loading && <Spinner />}

            {/* Contenedor sticky para Location y Breadcrumb */}
            <div className="sticky-header">
                <Location onLocationChange={handleLocationChange} />
                <Breadcrumb items={[{ label: "Inicio", icon: <Home size={16} />, link: "/" }, { label: type }]} />
            </div>

            <div className={`categories-container ${type === "servicios" ? "type-servicios" : "type-productos"}`}>
                <h2 className="page-title">
                    {type === "servicios" ? "Servicios" : "Productos"}
                </h2>


                {/* Componente de búsqueda con autocompletado */}
                <div className="search-container">
                    <AutocompleteSearch
                        searchQuery={searchQuery}
                        searchResults={searchResults}
                        handleSearchQueryChange={handleSearchQueryChange}
                        handleKeyPress={handleKeyPress}
                        handleResultSelect={handleResultSelect}
                        disabled={isUpdating || loading}
                    />
                </div>

                {/* Banner de proveedores destacados*/}
                <FeaturedProvidersBanner categoryType={type} />

                {/* Controles de ordenamiento: debajo de la búsqueda y del banner */}
                <div className="categories-controls">
                    <span className="sort-label-text">Orden</span>
                    <div className="sort-toggle" data-active={sortMode}>
                        <button
                            className={`sort-btn ${sortMode === 'alpha' ? 'active' : ''}`}
                            onClick={() => setSortMode('alpha')}
                            title="Ordenar alfabéticamente"
                            aria-pressed={sortMode === 'alpha'}
                        >
                            <List size={15} />
                            <span>A → Z</span>
                        </button>
                        <button
                            className={`sort-btn ${sortMode === 'count' ? 'active' : ''}`}
                            onClick={() => setSortMode('count')}
                            title="Ordenar por mayor a menor cantidad de proveedores"
                            aria-pressed={sortMode === 'count'}
                        >
                            <BarChart2 size={15} />
                            <span>Cantidad</span>
                        </button>
                    </div>
                </div>

                <div className="categories2-grid">
                    {(() => {
                        // Crear una copia ordenada según sortMode
                        const cats = [...categories];
                        if (sortMode === 'alpha') {
                            cats.sort((a, b) => a.name.localeCompare(b.name));
                        } else if (sortMode === 'count') {
                            cats.sort((a, b) => {
                                const aCount = renderProviderCount(a) || 0;
                                const bCount = renderProviderCount(b) || 0;
                                return bCount - aCount; // mayor a menor
                            });
                        }
                        return cats.map((category) => (
                            <div
                                key={category.name}
                                className={`category-card ${type === "servicios" ? "service-category" : "product-category"} ${isUpdating ? "disabled" : ""}`}
                                onClick={() => !isUpdating && !loading && handleCategoryClick(category.name, category.type)}
                                style={{ pointerEvents: isUpdating || loading ? 'none' : 'auto' }}
                            >
                                <h3>{category.name}</h3>
                                {selectedLocationObj && (
                                    <p className="provider-count">
                                        {renderProviderCount(category)} proveedor
                                        {renderProviderCount(category) !== 1 && "es"}
                                    </p>
                                )}
                            </div>
                        ))
                    })()}
                    <div
                        className={`category-card new-category ${type === "servicios" ? "service-category" : "product-category"} ${isUpdating ? "disabled" : ""}`}
                        onClick={handleShowSuggestionModal}
                        style={{ pointerEvents: isUpdating || loading ? 'none' : 'auto' }}
                    >
                        <h3>¿No encontraste la categoría que buscabas?</h3>
                    </div>
                </div>
            </div>

            {showSuggestionModal && (
                <div className="categories-modal-overlay" onClick={handleCloseSuggestionModal}>
                    <div className="categories-modal-popup" onClick={(e) => e.stopPropagation()}>
                        <button className="categories-modal-close" onClick={handleCloseSuggestionModal}>
                            ×
                        </button>
                        <h2 className="categories-modal-title">Sugerir nueva categoría</h2>
                        <form className="categories-modal-form" onSubmit={handleSubmit}>
                            <input
                                className="categories-modal-input"
                                type="text"
                                value={newCategory.name}
                                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                                placeholder="Nombre de la categoría"
                                required
                            />
                            <textarea
                                className="categories-modal-textarea"
                                rows="3"
                                value={newCategory.subcategories}
                                onChange={(e) => setNewCategory({ ...newCategory, subcategories: e.target.value })}
                                placeholder="Posibles subcategorías (separadas por comas)"
                            ></textarea>
                            <button
                                type="submit"
                                className="categories-modal-submit"
                                style={{
                                    backgroundColor:(type==="products")?"#0056b3":"#cc1e00",
                                    opacity: loading ? 0.7 : 1
                                }}
                                disabled={loading}
                            >
                                {loading ? "Enviando..." : "Enviar"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Categories
